/*
 * Der Live-Adapter: `IPointBindingSource` auf den echten Datenpfad.
 *
 * Bis hierher gab es genau zwei Implementierungen des Interfaces, beide
 * Simulator (`hmi/InMemorySource.ts` und die tote C#-Variante). Live-Daten
 * liefen über einen völlig getrennten Stapel — `realtime/hubClient.ts` →
 * `realtime/signalStore.ts` für Werte, `realtime/writeStore.ts` +
 * `realtime/commandsStore.ts` für Schreibvorgänge. Diese Klasse ist die
 * Brücke, und sie baut absichtlich KEINEN zweiten Datenpfad:
 *
 *   - Lesen: `PlantLiveState.leafSignal(leafId)` aus dem Signal-Store. Damit
 *     erbt das HMI die Refcount-Abos, die 1,5-s-Karenz gegen Abo-Flattern,
 *     das Visibility-Gating und den Re-Subscribe nach Reconnect, ohne eine
 *     Zeile davon zu duplizieren.
 *   - Schreiben: derselbe HTTP-Endpoint und dieselben zwei Stores wie der
 *     Datenpunkt-Baum (`components/SendCommandModal.tsx`). Ein HMI-Klick und
 *     ein Klick im DP-Viewer erzeugen damit denselben Audit-Eintrag,
 *     denselben Toast und dieselbe Zwei-Stufen-Bestätigung.
 *   - Historie: `GET /api/leaves/{leafId}/history`, derselbe Endpoint, den
 *     `components/LeafHistoryChart.tsx` benutzt.
 *
 * Was diese Klasse selbst tut, ist die Übersetzung — und zwar die zwei
 * Übersetzungen, an denen es später still falsch wird:
 *
 *   1. Binding-Key → (plantId, leafId). Siehe `hmi/bindingKey.ts`.
 *   2. Qualität und Alter → `PointValue.quality`. Siehe `hmi/quality.ts`.
 *      Ein 40 Minuten alter Wert kommt hier als `stale` heraus, auch wenn
 *      der Server zuletzt `Good` gesagt hat, weil der Server bei Deadband
 *      gar keine Nachricht schickt und deshalb auch kein `Stale` nachliefert.
 *
 * Nicht verifiziert am laufenden System: dieser Adapter ist gegen den
 * Server-Quellcode und die korrigierte Spec (§5.2) gebaut, aber nie gegen
 * eine echte Cloud gelaufen. Was ein Deploy zeigen muss, steht am Ende der
 * Datei.
 */

import { correlationId as newCorrelationId } from './correlationId';
import { signal, effect, type Signal } from '@preact/signals';
import { api, ApiError } from '../api/client';
import type { SendCommandRequest, SendCommandResponse } from '../api/types';
import { historyPath, toSeries, type HistoryRequest, type LeafHistoryResponse } from '../api/history';
import { getConnectionState, type ConnectionState } from '../realtime/hubClient';
import type { LeafValue, PlantLiveState } from '../realtime/signalStore';
import { registerWrite, reportValue, writeSignalFor, type WriteState as StoreWriteState } from '../realtime/writeStore';
import { trackCommand } from '../realtime/commandsStore';
import type {
  HistoryAggregation,
  IPointBindingSource,
  PointHistorySample,
  PointValue,
  WriteAcknowledgement,
} from './binding';
import { parseBindingKey, type PointBindingKey } from './bindingKey';
import { projectLeafValue, unresolvedPointValue } from './quality';
import { staleAfterMsFor, type HmiLeafInfo } from './staleness';

// ---------------------------------------------------------------- Alterung
//
// Ein Wert, der nur alt wird, erzeugt kein Signal-Update — es kommt ja
// nichts an. Ohne eine externe Uhr würde ein Widget einen `Good`-Wert
// unbegrenzt lange als frisch anzeigen, weil nie ein Re-Render ausgelöst
// wird. Dieser Ticker ist diese Uhr: ein Signal, das im Sekundentakt-Bereich
// hochzählt und von jedem offenen Abo mitgelesen wird. Kippt ein Wert über
// die Stale-Schwelle, läuft der Effekt neu, `projectLeafValue` liefert
// `stale`, und das Widget dimmt.
//
// Läuft nur, solange mindestens ein Abo offen ist — ein leerer HMI-Tab hält
// keinen Timer.

const AGE_TICK_INTERVAL_MS = 5_000;
const ageTick: Signal<number> = signal(0);
let ageTickHandle: ReturnType<typeof setInterval> | null = null;
let ageTickRefs = 0;

function acquireAgeTick(): () => void {
  ageTickRefs += 1;
  if (ageTickHandle === null) {
    ageTickHandle = setInterval(() => {
      ageTick.value = ageTick.peek() + 1;
    }, AGE_TICK_INTERVAL_MS);
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    ageTickRefs -= 1;
    if (ageTickRefs <= 0 && ageTickHandle !== null) {
      clearInterval(ageTickHandle);
      ageTickHandle = null;
      ageTickRefs = 0;
    }
  };
}

// ---------------------------------------------------------------- Optionen

/**
 * Nachschlagen des Live-Zustands einer Anlage.
 *
 * Die Klasse kann `useLivePlant` nicht selbst aufrufen — das ist ein
 * Preact-Hook und gehört in eine Komponente. Der Provider
 * (`hmi/PointBindingProvider.tsx`) hält die Hooks und gibt diese Funktion
 * herein. `null` heißt "für diese Anlage ist hier kein Abo offen"; der
 * Adapter macht daraus einen sichtbaren `bad`-Wert statt eines endlosen
 * Spinners.
 */
export type PlantLiveLookup = (plantId: string) => PlantLiveState | null;

export interface SignalRSourceOptions {
  lookupPlant: PlantLiveLookup;
  /** Hub-Verbindungszustand. Default: das globale Signal aus `hubClient`. */
  connection?: Signal<ConnectionState>;
  /** Uhr. Injizierbar, damit Tests Alterung ohne Warten prüfen können. */
  now?: () => number;
  /**
   * Schema-Angaben je Leaf, aus dem Anlagen-Schema.
   *
   * Zwei Dinge hängen daran, und beide gehören dem Datenpunkt und nicht
   * dieser Klasse:
   *
   *   - `label` für den Command-Toast. Ohne das steht dort der nackte Guid.
   *   - `pollIntervalSeconds` / `heartbeatSeconds` für die Alters-Schwelle
   *     (`hmi/staleness.ts`). Ohne das gilt der konservative Fallback von
   *     zehn Minuten — nicht 60 Sekunden, denn eine feste Minute dimmt
   *     jeden Datenpunkt mit trägem Poll dauerhaft.
   *
   * Ein Lookup statt zweier Callbacks, damit der Provider EINE stabile
   * Funktion hereingibt und die Quelle nicht bei jedem Schema-Nachladen neu
   * gebaut werden muss.
   */
  leafInfoFor?: (leafId: string) => HmiLeafInfo | undefined;
  /**
   * Sollen Schreibvorgänge im globalen Command-Store landen (Toast +
   * clientseitiger 45-s-Timeout)? Default `true` — genau wie im
   * Datenpunkt-Baum. Ein Kiosk-Panel ohne Toaster kann das abschalten.
   */
  trackCommands?: boolean;
}

// ---------------------------------------------------------------- Adapter

export class SignalRPointBindingSource implements IPointBindingSource {
  private readonly lookupPlant: PlantLiveLookup;
  private readonly connection: Signal<ConnectionState>;
  private readonly now: () => number;
  private readonly leafInfoFor: (leafId: string) => HmiLeafInfo | undefined;
  private readonly trackCommands: boolean;
  /**
   * Gemerkte Alters-Schwelle je Leaf. Die Ableitung ist billig, aber der
   * Alterungs-Ticker läuft sie sonst für jeden abonnierten Punkt alle fünf
   * Sekunden neu durch — und das Ergebnis ändert sich nur, wenn sich das
   * Schema ändert (und dann baut der Provider eine neue Quelle).
   */
  private readonly staleAfterMsCache = new Map<string, number>();

  constructor(options: SignalRSourceOptions) {
    this.lookupPlant = options.lookupPlant;
    this.connection = options.connection ?? getConnectionState();
    this.now = options.now ?? (() => Date.now());
    this.leafInfoFor = options.leafInfoFor ?? (() => undefined);
    this.trackCommands = options.trackCommands ?? true;
  }

  /**
   * Alters-Schwelle dieses Datenpunkts in Millisekunden.
   *
   * Öffentlich, weil die UI sie für den Tooltip an einer gedimmten Kachel
   * braucht: "veraltet ab 3 min" macht den Techniker handlungsfähig, ein
   * nackter Graustich nicht.
   */
  staleAfterMs(leafId: string): number {
    const cached = this.staleAfterMsCache.get(leafId);
    if (cached !== undefined) return cached;
    const derived = staleAfterMsFor(this.leafInfoFor(leafId));
    this.staleAfterMsCache.set(leafId, derived);
    return derived;
  }

  // ------------------------------------------------------------ subscribe

  async *subscribe(pointId: string, abortSignal: AbortSignal): AsyncIterable<PointValue> {
    const key = parseBindingKey(pointId);
    if (!key) {
      // Ein nicht auflösbarer Key ist ein Fehler im Projekt, nicht in der
      // Anlage. Sichtbar machen und den Stream beenden: der `bad`-Wert
      // bleibt im Signal stehen, das Widget zeigt dauerhaft "ungültig".
      console.error('[hmi/SignalRSource] Binding-Key nicht auflösbar', pointId);
      yield unresolvedPointValue(this.now());
      return;
    }

    const live = this.lookupPlant(key.plantId);
    if (!live) {
      console.error(
        '[hmi/SignalRSource] kein Live-Abo für Anlage — Binding wird nicht bedient',
        { pointId, plantId: key.plantId },
      );
      yield unresolvedPointValue(this.now());
      return;
    }

    const leafSignal = live.leafSignal(key.leafId);
    // Einmal je Abo ermitteln, nicht je Tick. Siehe staleAfterMsCache.
    const staleAfterMs = this.staleAfterMs(key.leafId);
    const queue: PointValue[] = [];
    let waker: (() => void) | null = null;
    let last: PointValue | null = null;

    const releaseTick = acquireAgeTick();
    const disposeEffect = effect(() => {
      const leaf = leafSignal.value;
      const connection = this.connection.value;
      // Abhängigkeit auf die Alterungs-Uhr. Ohne diesen Lesezugriff läuft
      // der Effekt nur bei neuen Werten, und ein alternder Wert bliebe für
      // immer `good`.
      void ageTick.value;
      if (!leaf) return;
      const projected = projectLeafValue(leaf, connection, staleAfterMs, this.now());
      if (last !== null && samePointValue(last, projected)) return;
      last = projected;
      queue.push(projected);
      waker?.();
    });

    const onAbort = () => waker?.();
    abortSignal.addEventListener('abort', onAbort);

    try {
      while (!abortSignal.aborted) {
        while (queue.length > 0 && !abortSignal.aborted) {
          yield queue.shift()!;
        }
        if (abortSignal.aborted) break;
        await new Promise<void>((resolve) => { waker = resolve; });
        waker = null;
      }
    } finally {
      disposeEffect();
      releaseTick();
      abortSignal.removeEventListener('abort', onAbort);
    }
  }

  // ---------------------------------------------------------------- write

  async *write(
    pointId: string,
    value: unknown,
    abortSignal: AbortSignal,
  ): AsyncIterable<WriteAcknowledgement> {
    const key = parseBindingKey(pointId);
    if (!key) {
      console.error('[hmi/SignalRSource] Schreib-Key nicht auflösbar', pointId);
      yield {
        correlationId: newCorrelationId(),
        state: 'rejected',
        echoValue: null,
        error: 'Binding-Key nicht auflösbar — das HMI-Projekt zeigt auf keinen Datenpunkt.',
        at: new Date(this.now()).toISOString(),
      };
      return;
    }

    // Kommando abschicken BEVOR `pending` gemeldet wird. Der Server vergibt
    // die correlationId, und ein `pending` mit einer lokal erfundenen Id
    // gefolgt von Terminal-Events mit einer anderen Id wäre für jeden
    // Konsumenten irreführend. Die Verzögerung ist ein HTTP-Roundtrip; die
    // Renderer setzen ihren Spinner ohnehin aus lokalem State, bevor sie
    // den ersten Ack abwarten.
    let correlationId: string;
    try {
      const response = await api.post<SendCommandResponse>(
        `/api/plants/${key.plantId}/leaves/${key.leafId}/commands/set`,
        { value, mode: 'Normal' } satisfies SendCommandRequest,
        abortSignal,
      );
      correlationId = response.correlationId;
    } catch (err) {
      if (abortSignal.aborted) return;
      yield {
        correlationId: newCorrelationId(),
        state: 'rejected',
        echoValue: null,
        error: describeApiError(err),
        at: new Date(this.now()).toISOString(),
      };
      return;
    }

    // Dieselben zwei Stores wie der Datenpunkt-Baum. `registerWrite` treibt
    // die Zwei-Stufen-Bestätigung (Ack → Readback), `trackCommand` den
    // Toast und den clientseitigen Timeout.
    registerWrite({ leafId: key.leafId, correlationId, sentValue: value });
    if (this.trackCommands) {
      trackCommand({
        correlationId,
        plantId: key.plantId,
        leafId: key.leafId,
        leafSymbol: this.leafInfoFor(key.leafId)?.label ?? key.leafId,
        valueLabel: formatValueLabel(value),
      });
    }

    yield {
      correlationId,
      state: 'pending',
      echoValue: null,
      at: new Date(this.now()).toISOString(),
    };

    yield* this.awaitWriteOutcome(key, correlationId, abortSignal);
  }

  /**
   * Übersetzt die Stufen aus `writeStore` in die drei Zustände des
   * Interface-Vertrags.
   *
   * | writeStore   | WriteAcknowledgement                                  |
   * | ------------ | ----------------------------------------------------- |
   * | `sending`    | (schon als `pending` gemeldet, wird übersprungen)     |
   * | `acked`      | `pending` — Server hat, Regler noch nicht bestätigt   |
   * | `confirmed`  | `confirmed`, `echoValue` = Readback                   |
   * | `mismatched` | `rejected`, `echoValue` = was der Regler wirklich hält |
   * | `failed`     | `rejected` mit Fehlertext                             |
   *
   * `mismatched` ist bewusst `rejected` und nicht `confirmed`: der Regler
   * hat das Kommando angenommen, hält aber einen anderen Wert
   * (Priority-Array, Rampe, Override). Für ein Widget ist das ein
   * Fehlschlag — der Sollwert, den der Operator gesetzt hat, gilt nicht.
   * Der `echoValue` sagt, was stattdessen gilt, damit ein Toggle den echten
   * Zustand zeigen kann.
   *
   * Zusätzlich wird jeder frische Live-Wert dieses Leaves an
   * `reportValue` gemeldet. Ohne das bliebe ein Schreibvorgang ohne
   * Immediate-Readback (Setpoint-Rampe, write-only Register) für immer in
   * `acked` hängen: im Datenpunkt-Baum macht `LeafValueDisplay` diese
   * Meldung, und im HMI gibt es diese Komponente nicht.
   */
  private async *awaitWriteOutcome(
    key: PointBindingKey,
    correlationId: string,
    abortSignal: AbortSignal,
  ): AsyncIterable<WriteAcknowledgement> {
    const writeSignal = writeSignalFor(key.leafId);
    const live = this.lookupPlant(key.plantId);
    const leafSignal = live ? live.leafSignal(key.leafId) : null;

    const queue: WriteAcknowledgement[] = [];
    let waker: (() => void) | null = null;
    let terminal = false;
    // Startet als `null`, damit die ERSTE beobachtete Stufe gemeldet wird —
    // auch wenn der Regler so schnell antwortet, dass `confirmed` schon
    // steht, bevor dieser Effekt läuft. Der einzige verbleibende Spalt: der
    // `writeStore` räumt seinen Zustand nach 3,5 s (Erfolg) bzw. 8 s
    // (Fehler) auf `null`. Wer den `pending`-Ack länger als das nicht
    // abholt, sieht kein Terminal-Event mehr und hängt bis zum Abbruch in
    // `pending`. Ein Renderer iteriert sofort weiter, also passiert das in
    // der Praxis nicht; der Toast des Command-Stores zeigt den Ausgang
    // ohnehin unabhängig davon.
    let lastStage: StoreWriteState['stage'] | null = null;
    let lastReportedLeaf: LeafValue | null = null;

    const disposeEffect = effect(() => {
      // Frische Werte an den writeStore melden, damit `acked` → `confirmed`
      // auch ohne Immediate-Readback kippen kann.
      if (leafSignal) {
        const leaf = leafSignal.value;
        if (leaf && leaf !== lastReportedLeaf) {
          lastReportedLeaf = leaf;
          reportValue(key.leafId, leaf.v);
        }
      }

      const state = writeSignal.value;
      if (!state || state.correlationId !== correlationId) return;
      if (state.stage === lastStage) return;
      lastStage = state.stage;
      const ack = toAcknowledgement(state, correlationId, this.now());
      if (!ack) return;
      queue.push(ack);
      if (ack.state !== 'pending') terminal = true;
      waker?.();
    });

    const onAbort = () => waker?.();
    abortSignal.addEventListener('abort', onAbort);

    try {
      while (!abortSignal.aborted) {
        while (queue.length > 0 && !abortSignal.aborted) {
          yield queue.shift()!;
        }
        if (terminal || abortSignal.aborted) break;
        await new Promise<void>((resolve) => { waker = resolve; });
        waker = null;
      }
    } finally {
      disposeEffect();
      abortSignal.removeEventListener('abort', onAbort);
    }
    // Anmerkung zum Abbruch: ein `abort` beendet nur diesen Stream. Das
    // Kommando ist unterwegs und wird nicht zurückgerufen — es gibt keinen
    // Endpoint dafür, und es wäre auch falsch, so zu tun. Der globale
    // Command-Store zeigt den Ausgang weiter im Toast.
  }

  // ---------------------------------------------------------- getHistory

  async getHistory(
    pointId: string,
    since: Date,
    until: Date,
    aggregation: HistoryAggregation,
    abortSignal: AbortSignal,
    widthPx?: number,
  ): Promise<PointHistorySample[]> {
    const key = parseBindingKey(pointId);
    if (!key) {
      console.error('[hmi/SignalRSource] History-Key nicht auflösbar', pointId);
      return [];
    }

    const interval = HISTORY_INTERVALS[aggregation];
    const request: HistoryRequest = interval
      ? { mode: 'exact', interval }
      : { mode: 'shape', widthPx: widthPx ?? DEFAULT_SHAPE_WIDTH_PX };

    try {
      const response = await api.get<LeafHistoryResponse>(
        historyPath(key.leafId, since, until, request),
        abortSignal,
      );
      // Step points: shape points as delivered, exact interval values with
      // explicit interval ends and gaps (see api/history.ts).
      return toSeries(response).map(([t, v]) => ({
        timestamp: new Date(t).toISOString(),
        value: v,
        quality: 'good' as const,
      }));
    } catch (err) {
      if (abortSignal.aborted) return [];
      // Der Vertrag verlangt: Fehler kommen über den Wert-Stream oder als
      // Rejected, nie als Exception. Eine leere Historie zeichnet einen
      // leeren Trend — sichtbar, aber ohne den Renderer zu sprengen.
      if (err instanceof ApiError && err.status === 404) return [];
      console.error('[hmi/SignalRSource] History-Abruf fehlgeschlagen', { pointId, err });
      return [];
    }
  }
}

// ---------------------------------------------------------------- Helfer

/**
 * Interval per aggregation level, as .NET `TimeSpan` string. `auto` and
 * `raw` have none: they request the shape (M4), which shows the raw data
 * pixel-identically at any window length.
 *
 * `1d` is `1.00:00:00`, not `24:00:00` — .NET does not parse the latter
 * (the hours component is limited to 0..23).
 */
const HISTORY_INTERVALS: Record<HistoryAggregation, string | null> = {
  auto: null,
  raw: null,
  '1m': '00:01:00',
  '5m': '00:05:00',
  '1h': '01:00:00',
  '1d': '1.00:00:00',
};

/** Shape width when the caller does not know its chart width. */
const DEFAULT_SHAPE_WIDTH_PX = 1000;

function samePointValue(a: PointValue, b: PointValue): boolean {
  return a.quality === b.quality
    && a.timestamp === b.timestamp
    && a.receivedAt === b.receivedAt
    && Object.is(a.value, b.value);
}

function toAcknowledgement(
  state: StoreWriteState,
  correlationId: string,
  now: number,
): WriteAcknowledgement | null {
  const at = new Date(now).toISOString();
  switch (state.stage) {
    case 'sending':
      // Schon vor dem Warten als `pending` gemeldet.
      return null;
    case 'acked':
      return { correlationId, state: 'pending', echoValue: null, at };
    case 'confirmed':
      return {
        correlationId,
        state: 'confirmed',
        echoValue: state.actualValue ?? state.sentValue,
        at,
      };
    case 'mismatched':
      return {
        correlationId,
        state: 'rejected',
        echoValue: state.actualValue ?? null,
        error: `Regler hält ${formatValueLabel(state.actualValue)} statt ${formatValueLabel(state.sentValue)}.`,
        at,
      };
    case 'failed':
      return {
        correlationId,
        state: 'rejected',
        echoValue: null,
        error: state.errorMessage ?? 'Der Schreibvorgang ist fehlgeschlagen.',
        at,
      };
  }
}

function formatValueLabel(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'An' : 'Aus';
  if (typeof value === 'number') return String(value);
  return String(value);
}

function describeApiError(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.status) {
      case 403: return 'Keine Berechtigung für diesen Schreibvorgang.';
      case 404: return 'Datenpunkt nicht gefunden.';
      case 409: return 'Datenpunkt ist nicht beschreibbar.';
      default:  return `Server antwortete mit ${err.status}.`;
    }
  }
  return err instanceof Error ? err.message : 'Unbekannter Fehler.';
}

/*
 * ----------------------------------------------------------------------
 * Was erst am laufenden System belegt werden kann
 * ----------------------------------------------------------------------
 *
 *  - Ob `PlantLiveState.leafSignal(leafId)` für die Leaf-Guids eines
 *    HMI-Projekts tatsächlich Werte bekommt. Das hängt daran, dass der
 *    Server für diese Leaves auch publisht — er publisht nur, was er hat.
 *  - Ob der Command-Pfad aus dem HMI dieselben Events sieht wie aus dem
 *    DP-Viewer. Der Hub broadcastet auf `commands-{userId}`, also sollte er;
 *    geprüft ist es nicht.
 *  - Ob die pro Datenpunkt abgeleitete Alters-Schwelle
 *    (`hmi/staleness.ts`) im Feld passt. Die beiden Zweifel, die hier bis
 *    zum 03.09. standen — der Heartbeat-Kanal sei nicht implementiert, und
 *    `heartbeatSeconds` sei an realen Anlagen nicht gesetzt —, sind beide
 *    erledigt: das CoV-Gate liest ihn seit Telemetrie-Phase 1b in beiden
 *    Clients, und seit der Migration vom 09.09. trägt jeder aufgezeichnete
 *    Datenpunkt 900 s. Offen ist damit nur noch die Grenze selbst: die
 *    Schwelle liegt jetzt bei 45 Minuten (drei ausgefallene Zyklen), und
 *    ein toter Feld-Client fällt datenpunktseitig entsprechend später auf
 *    als mit dem alten Zehn-Minuten-Fallback. Die schnellere Aussage dazu
 *    ist der Client-Lebenszeichen-Pfad (`clients.last_seen_at`, alle fünf
 *    Minuten), nicht diese Schwelle.
 */
